import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Mail, Lock, User, MapPin, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function Register() {
    const { user, register } = useAuth();
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [location, setLocation] = useState("Ireland");
    const [climate, setClimate] = useState("temperate maritime");
    const [seedDemo, setSeedDemo] = useState(true);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    if (user && user !== false) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await register({
                name, email, password,
                location, climate_zone: climate,
                seed_demo: seedDemo,
            });
            // New signups go straight to /pricing so they pick a plan +
            // start their 4-day free trial. The free tier is closed to
            // new signups (existing free users keep their access).
            navigate("/pricing?welcome=1", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen paper-texture flex items-center justify-center p-6 py-10" data-testid="register-page">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <Logo variant="icon" size={56} />
                    <div>
                        <div className="font-serif-display text-3xl text-[var(--vm-text)] leading-none lowercase tracking-tight">rooteed</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)] mt-1">Garden Journal</div>
                    </div>
                </div>

                <div className="journal-card p-8">
                    <h1 className="font-serif-display text-3xl text-[var(--vm-text)] mb-2">Plant your first seed</h1>
                    <p className="text-sm text-[var(--vm-text-muted)] mb-6">
                        Create your account to start mapping beds and tracking crops.
                    </p>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label htmlFor="name" className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Name</Label>
                            <div className="relative mt-1">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vm-text-muted)]" />
                                <Input
                                    id="name" required autoFocus
                                    data-testid="register-name-input"
                                    value={name} onChange={(e) => setName(e.target.value)}
                                    className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                    placeholder="Your name"
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Email</Label>
                            <div className="relative mt-1">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vm-text-muted)]" />
                                <Input
                                    id="email" type="email" required autoComplete="email"
                                    data-testid="register-email-input"
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                    placeholder="you@garden.com"
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Password</Label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vm-text-muted)]" />
                                <Input
                                    id="password" type="password" required minLength={6} autoComplete="new-password"
                                    data-testid="register-password-input"
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                    placeholder="At least 6 characters"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="location" className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Location</Label>
                                <div className="relative mt-1">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vm-text-muted)]" />
                                    <Input
                                        id="location"
                                        data-testid="register-location-input"
                                        value={location} onChange={(e) => setLocation(e.target.value)}
                                        className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                        placeholder="Ireland"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="climate" className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Climate</Label>
                                <Input
                                    id="climate"
                                    data-testid="register-climate-input"
                                    value={climate} onChange={(e) => setClimate(e.target.value)}
                                    className="mt-1 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                    placeholder="temperate maritime"
                                />
                            </div>
                        </div>

                        <div className="flex items-start gap-3 journal-card p-4 mt-2 border-[var(--vm-border)]">
                            <Sprout className="w-5 h-5 text-[var(--vm-accent)] mt-0.5" strokeWidth={1.5} />
                            <div className="flex-1">
                                <div className="text-sm font-medium text-[var(--vm-text)]">Start with a demo garden</div>
                                <p className="text-xs text-[var(--vm-text-muted)] mt-0.5">
                                    Pre-fill a sample garden with one bed, four crops, a water butt and a compost bin so you can explore. Turn off for a clean slate.
                                </p>
                            </div>
                            <Switch
                                checked={seedDemo}
                                onCheckedChange={setSeedDemo}
                                data-testid="register-seed-demo-switch"
                            />
                        </div>

                        {error && (
                            <div className="text-sm text-[var(--vm-secondary)] bg-[var(--vm-secondary)]/10 rounded px-3 py-2" data-testid="register-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit" disabled={busy}
                            data-testid="register-submit-button"
                            className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] py-5"
                        >
                            {busy ? "Sowing your account…" : "Create account"}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-[var(--vm-text-muted)] text-center">
                        Already growing?{" "}
                        <Link to="/login" data-testid="login-link" className="text-[var(--vm-accent)] underline">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
